'use client';

import { ContentUnavailable, resolveDetailState } from '@/contexts/application/components/ContentUnavailable';
import { useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { ArrowLeft, Play, Square, Mic, Loader2, RefreshCw, Music, AlertTriangle, ChevronDown, ChevronUp, Heart } from 'lucide-react';
import { useAudioStory } from '@/contexts/audio-stories/hooks/useAudioStory';
import { useToggleAudioStoryFavorite } from '@/contexts/audio-stories/hooks/useToggleAudioStoryFavorite';
import { useStory } from '@/contexts/stories';
import { generateBlockAudio, generateBatchAudio, assembleAudio } from '@/contexts/audio-stories/actions/generation';
import { useWorkflowRun } from '@/shared/hooks/useWorkflowRun';
import { uploadBlockRecording } from '@/contexts/audio-stories/actions/recording';
import { updateVoiceSettings } from '@/contexts/audio-stories/actions/updateVoiceSettings';
import { VoiceSelector } from '@/contexts/stories';
import { AudioPlayer } from '@/contexts/media';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Spinner } from '@/shared/components/ui/spinner';
import type { AudioBlock, AssemblyStatus } from '@/contexts/audio-stories/types';
import { isAudioBlockStale } from '@/contexts/audio-stories/utils/staleness';

interface AudioStoryEditorProps {
  audioStoryId: string;
}

function AssemblyStatusBadge({ status }: { status: AssemblyStatus }) {
  const t = useTranslations('audioStories.editor.assembly');
  const variants: Record<AssemblyStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    ready: 'default',
    generating: 'secondary',
    assembling: 'secondary',
    error: 'destructive',
    no_audio: 'outline',
  };
  const labels: Record<AssemblyStatus, string> = {
    ready: t('statusReady'),
    generating: t('statusGenerating'),
    assembling: t('statusAssembling'),
    error: t('statusError'),
    no_audio: t('statusNoAudio'),
  };
  return (
    <Badge variant={variants[status]} data-testid="audio-editor-assembly-status-indicator">
      {labels[status]}
    </Badge>
  );
}

function AudioBlockItem({
  block,
  index,
  audioStoryId,
  textContent,
  textContentVersion,
}: {
  block: AudioBlock;
  index: number;
  audioStoryId: string;
  textContent?: string;
  /** text_blocks.content_version of the source block — staleness is derived from it. */
  textContentVersion?: number;
}) {
  const t = useTranslations('audioStories.editor');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenerateError(null);
    try {
      await generateBlockAudio(audioStoryId, block.blockId);
    } catch (err: unknown) {
      setGenerateError(err instanceof Error ? err.message : t('unknownError'));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-2 p-3 rounded-lg border bg-card"
      data-testid={`audio-editor-block-item-${block.blockId}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {t('blockLabel', { number: index + 1 })}
          </span>
          {isAudioBlockStale(textContentVersion, block) && (
            <Badge variant="outline" className="text-xs text-warning" data-testid={`audio-editor-block-stale-indicator-${block.blockId}`}>
              <AlertTriangle className="size-3 mr-1" />
              {t('blockStale')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerate}
            disabled={isGenerating}
            data-testid={`audio-editor-block-generate-${block.blockId}-button`}
          >
            {isGenerating ? (
              <Loader2 className="size-3 animate-spin mr-1" />
            ) : block.audioUrl ? (
              <RefreshCw className="size-3 mr-1" />
            ) : (
              <Play className="size-3 mr-1" />
            )}
            {block.audioUrl ? t('blockRegenerate') : t('blockGenerate')}
          </Button>
        </div>
      </div>
      {/* Story text preview */}
      <div
        className="cursor-pointer select-none"
        onClick={() => setIsTextExpanded((v) => !v)}
        data-testid={`audio-editor-block-text-${block.blockId}`}
      >
        <p className={`text-sm text-muted-foreground ${isTextExpanded ? '' : 'line-clamp-3'}`}>
          {textContent || t('blockTextUnavailable')}
        </p>
        {textContent && textContent.length > 200 && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground/70 hover:text-muted-foreground mt-1"
          >
            {isTextExpanded ? (
              <><ChevronUp className="size-3" />{t('blockTextCollapse')}</>
            ) : (
              <><ChevronDown className="size-3" />{t('blockTextExpand')}</>
            )}
          </button>
        )}
      </div>
      {block.audioUrl && (
        <AudioPlayer audioUrl={block.audioUrl} duration={block.duration ?? undefined} />
      )}
      {!block.audioUrl && !isGenerating && (
        <p className="text-sm text-muted-foreground">{t('blockNoAudio')}</p>
      )}
      {generateError && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 rounded text-sm text-destructive" data-testid={`audio-editor-block-error-${block.blockId}`}>
          {generateError}
        </div>
      )}
    </div>
  );
}

export function AudioStoryEditor({ audioStoryId }: AudioStoryEditorProps) {
  const t = useTranslations('audioStories.editor');
  const tFav = useTranslations('audioStories.list.favorites');
  const intlLocale = useLocale();
  const { data: audioStory, isLoading, error, refetch } = useAudioStory(audioStoryId);
  const { data: story } = useStory(audioStory?.storyId);
  const toggleFavoriteMutation = useToggleAudioStoryFavorite(audioStory?.familyId);

  const textBlockMap = useMemo(() => {
    if (!story?.textBlocks) return {};
    const map: Record<string, { text: string; contentVersion: number }> = {};
    for (const tb of story.textBlocks) {
      map[tb.id] = { text: tb.text, contentVersion: tb.contentVersion };
    }
    return map;
  }, [story?.textBlocks]);

  // Resumable batch state: audio_stories.batch_run_id is the engine run's
  // startKey — the workflow run (not a local bool) is the source of truth, so
  // a reloaded/late-joining client still sees a running batch (same pattern
  // as books usePageGeneration).
  const batchRun = useWorkflowRun(
    audioStory?.batchRunId ? { startKey: audioStory.batchRunId } : null,
  );
  const [batchStarting, setBatchStarting] = useState(false);
  const isBatchGenerating = batchStarting || batchRun.status === 'running';
  const batchStep = batchRun.steps.find((s) => s.stepId === 'tts-blocks');
  const batchProgress =
    batchRun.status === 'running' && batchStep?.total
      ? { done: batchStep.done ?? 0, total: batchStep.total }
      : null;
  const [batchError, setBatchError] = useState<string | null>(null);
  const [isAssembling, setIsAssembling] = useState(false);
  const [assemblyError, setAssemblyError] = useState<string | null>(null);
  const [isVoiceUpdating, setIsVoiceUpdating] = useState(false);

  // Listen for SSE events to refresh data
  useEventSourceEvent('audio-stories.block.audio-completed', useCallback((payload: Record<string, unknown>) => {
    if (payload.audioStoryId === audioStoryId) {
      refetch();
    }
  }, [audioStoryId, refetch]));

  useEventSourceEvent('audio-stories.assembly.done', useCallback((payload: Record<string, unknown>) => {
    if (payload.audioStoryId === audioStoryId) {
      setIsAssembling(false);
      // Event router auto-invalidates audioStoryKeys.detail — no manual refetch needed
    }
  }, [audioStoryId]));

  const handleBatchGenerate = async () => {
    setBatchStarting(true);
    setBatchError(null);
    try {
      await generateBatchAudio(audioStoryId);
      // refetch picks up the new batch_run_id → useWorkflowRun takes over
      await refetch();
    } catch (err: unknown) {
      setBatchError(err instanceof Error ? err.message : t('unknownError'));
    } finally {
      setBatchStarting(false);
    }
  };

  const handleAssemble = async () => {
    setIsAssembling(true);
    setAssemblyError(null);
    try {
      const result = await assembleAudio(audioStoryId);
      if (!result.success) {
        setAssemblyError(result.error);
        setIsAssembling(false);
      }
    } catch (err: unknown) {
      setAssemblyError(err instanceof Error ? err.message : t('unknownError'));
      setIsAssembling(false);
    }
  };

  const handleVoiceSelect = async (voiceId: string, voiceName: string) => {
    if (!audioStory) return;
    setIsVoiceUpdating(true);
    try {
      await updateVoiceSettings(audioStoryId, {
        defaultVoiceId: voiceId,
        defaultVoiceName: voiceName,
      });
      refetch();
    } finally {
      setIsVoiceUpdating(false);
    }
  };

  const audioState = resolveDetailState({ data: audioStory, error, isLoading });

  // Loading state
  if (audioState === 'loading') {
    return (
      <div className="space-y-6" data-testid="audio-editor-loading-indicator">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  // Not found / not yours — getAudioStory answers null for both; retry can't help.
  if (audioState === 'unavailable') {
    return <ContentUnavailable kind="audioStory" />;
  }

  // Error state (real, transient failure)
  if (error || !audioStory) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12" data-testid="audio-editor-error-indicator">
        <p className="text-destructive">{t('error')}</p>
        <Button variant="outline" onClick={() => refetch()} data-testid="audio-editor-retry-button">
          {t('retry')}
        </Button>
      </div>
    );
  }

  const totalTextBlocks = story?.textBlocks?.length ?? 0;
  const allBlocksHaveAudio = totalTextBlocks > 0 &&
    audioStory.audioBlocks.length >= totalTextBlocks &&
    audioStory.audioBlocks.every((b) => !!b.audioUrl);

  return (
    <div className="space-y-6" data-testid="audio-editor-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link href={`/${intlLocale}/stories/${audioStory.storyId}`} data-testid="audio-editor-back-button">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold truncate">
            {audioStory.name || t('title')}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <AssemblyStatusBadge status={audioStory.assemblyStatus} />
          </div>
        </div>
        <button
          data-testid={`audio-story-favorite-toggle-${audioStoryId}`}
          onClick={() => toggleFavoriteMutation.mutate(audioStoryId)}
          className={`p-2 flex items-center justify-center transition-colors rounded-lg ${audioStory.isFavorite ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`}
          title={audioStory.isFavorite ? tFav('removeFromFavorites') : tFav('addToFavorites')}
          aria-label={audioStory.isFavorite ? tFav('removeFromFavorites') : tFav('addToFavorites')}
          aria-pressed={audioStory.isFavorite}
        >
          <Heart className={`size-5 ${audioStory.isFavorite ? 'fill-current' : ''}`} />
        </button>
      </div>

      {/* Voice Settings */}
      <Card data-testid="audio-editor-voice-settings-section">
        <CardHeader>
          <CardTitle>{t('voiceSettings')}</CardTitle>
        </CardHeader>
        <CardContent>
          <VoiceSelector
            selectedVoiceId={audioStory.defaultVoiceId ?? undefined}
            onVoiceSelect={handleVoiceSelect}
            disabled={isVoiceUpdating}
            familyId={audioStory.familyId}
            language={story?.language as any}
          />
        </CardContent>
      </Card>

      {/* Audio Blocks */}
      <Card data-testid="audio-editor-blocks-section">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>{t('blocks')}</CardTitle>
          <Button
            size="sm"
            onClick={handleBatchGenerate}
            disabled={isBatchGenerating || audioStory.audioBlocks.length === 0}
            data-testid="audio-editor-generate-all-button"
          >
            {isBatchGenerating ? (
              <Loader2 className="size-4 animate-spin mr-1" />
            ) : (
              <Play className="size-4 mr-1" />
            )}
            {isBatchGenerating
              ? batchProgress
                ? `${t('generating')} ${batchProgress.done}/${batchProgress.total}`
                : t('generating')
              : t('generateAll')}
          </Button>
        </CardHeader>
        <CardContent>
          {audioStory.audioBlocks.length === 0 ? (
            <div className="text-center py-8" data-testid="audio-editor-blocks-empty-indicator">
              <p className="text-muted-foreground">{t('noBlocks')}</p>
              <p className="text-sm text-muted-foreground mt-1">{t('noBlocksDescription')}</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="audio-editor-blocks-list">
              {audioStory.audioBlocks.map((block, index) => (
                <AudioBlockItem
                  key={block.id}
                  block={block}
                  index={index}
                  audioStoryId={audioStoryId}
                  textContent={textBlockMap[block.blockId]?.text}
                  textContentVersion={textBlockMap[block.blockId]?.contentVersion}
                />
              ))}
            </div>
          )}

          {batchError && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded text-sm text-destructive" data-testid="audio-editor-batch-error-indicator">
              {batchError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assembly Panel */}
      <Card data-testid="audio-editor-assembly-panel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="size-5" />
            {t('assembly.title')}
          </CardTitle>
          <p className="text-sm text-muted-foreground">{t('assembly.description')}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {audioStory.assembledAudioUrl && audioStory.assemblyInvalidated && (
            <div
              data-testid="audio-editor-assembly-invalidated-banner"
              className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/30 rounded text-sm text-warning-foreground"
            >
              <AlertTriangle className="size-4 mt-0.5 shrink-0 text-warning" />
              <span>{t('assembly.outdatedBanner')}</span>
            </div>
          )}
          <Button
            onClick={handleAssemble}
            disabled={isAssembling || !allBlocksHaveAudio}
            className="w-full"
            data-testid="audio-editor-assemble-button"
          >
            {isAssembling ? (
              <>
                <Loader2 className="size-4 animate-spin mr-2" />
                {t('assembly.assembling')}
              </>
            ) : audioStory.assembledAudioUrl ? (
              <>
                <RefreshCw className="size-4 mr-2" />
                {t('assembly.reassemble')}
              </>
            ) : (
              <>
                <Music className="size-4 mr-2" />
                {t('assembly.assemble')}
              </>
            )}
          </Button>

          {audioStory.assembledAudioUrl && (
            <div data-testid="audio-editor-assembled-audio">
              <AudioPlayer
                audioUrl={audioStory.assembledAudioUrl}
                title={audioStory.name || t('title')}
              />
            </div>
          )}

          {assemblyError && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded text-sm text-destructive" data-testid="audio-editor-assembly-error-indicator">
              {assemblyError}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
